import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createElementBlock as _createElementBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-58e5ca9d"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "point-page" }
const _hoisted_2 = { class: "point-page__row-actions" }
const _hoisted_3 = {
  key: 0,
  class: "point-page__dialog-summary"
}
const _hoisted_4 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { class: "el-upload__text" }, [
  /*#__PURE__*/_createTextVNode("将文件拖到此处，或"),
  /*#__PURE__*/_createElementVNode("em", null, "点击上传")
], -1))
const _hoisted_5 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { class: "el-upload__tip" }, " 支持 .xls、.xlsx 格式，不超过 20 MB，一次最多修改 100 个客户 ", -1))

import { computed, onMounted, reactive, ref, shallowRef } from 'vue'
import { ElForm, ElMessage, ElMessageBox } from 'element-plus'
import { Coin, Download, Upload } from '@element-plus/icons-vue'
import XLSX from 'xlsx'
import { CrudDialog, CrudSearch, CrudTable } from '@/components/crud-v2'
import { usePagedCrud } from '@/hooks/crud/usePagedCrud'
import { usePermission } from '@/hooks/usePermission'
import {
  createUserPointTableColumns,
  pointRecordTableColumns,
  pointSearchItems
} from './point.config'
import {
  batchUpdateUserPoint,
  changeUserPoint,
  fetchPointReasons,
  fetchPointRecordPage,
  fetchUserPointPage
} from './point.service'
import type {
  BatchUpdateResult,
  PointReason,
  UserPointRecord
} from './point.service'

type PointFlow = 0 | 1
type FormRef = InstanceType<typeof ElForm>

interface PointForm {
  reason: string
  changePoint: number | ''
}


export default /*#__PURE__*/_defineComponent({
  __name: 'Point',
  setup(__props) {

const canList = usePermission('userExpand', 'list')
const canUpdatePoint = usePermission('userExpand', 'updatePoint')
const canBatchEditPoint = usePermission('userExpand', 'batchEditPoint')
const userPointTableColumns = createUserPointTableColumns(canUpdatePoint)

const {
  filters,
  pageNo,
  pageSize,
  records,
  total,
  error,
  loading,
  load,
  search,
  reset,
  changePage,
  changePageSize,
  refresh
} = usePagedCrud({
  createFilters: () => ({ loginName: '', nickName: '' }),
  fetchPage: fetchUserPointPage
})

const {
  filters: recordFilters,
  pageNo: recordPageNo,
  pageSize: recordPageSize,
  records: pointRecords,
  total: recordTotal,
  error: recordError,
  loading: recordLoading,
  search: searchPointRecords,
  changePage: changeRecordPage,
  changePageSize: changeRecordPageSize
} = usePagedCrud({
  createFilters: () => ({ userId: 0, flow: 0 as PointFlow }),
  fetchPage: fetchPointRecordPage
})

const errorMessage = computed(() => getErrorMessage(error.value))
const recordErrorMessage = computed(() => getErrorMessage(recordError.value))

function getErrorMessage(value: unknown) {
  return value instanceof Error ? value.message : ''
}

const pointDialogVisible = ref(false)
const pointDialogTitle = ref('积分操作')
const pointFormRef = ref<FormRef>()
const pointFlow = ref<PointFlow>(1)
const selectedUser = shallowRef<UserPointRecord>()
const pointReasons = shallowRef<PointReason[]>([])
const pointForm = reactive<PointForm>({ reason: '', changePoint: '' })

const pointRules = {
  changePoint: [
    { required: true, message: '金币数是必传的', trigger: 'blur' },
    { validator: validateChangePoint, trigger: 'blur' }
  ]
}

function validateChangePoint(
  _rule: unknown,
  value: number | '',
  callback: (error?: Error) => void
) {
  const amount = Number(value)
  if (amount <= 0) {
    callback(new Error('金币数量必须大于零'))
    return
  }
  if (
    pointFlow.value === 0 &&
    selectedUser.value &&
    amount > Number(selectedUser.value.point)
  ) {
    callback(new Error('扣除金币输入金币数量不能超过用户拥有的金币数量'))
    return
  }
  callback()
}

async function openPointDialog(row: UserPointRecord, flow: PointFlow) {
  selectedUser.value = row
  pointFlow.value = flow
  pointDialogTitle.value = flow === 1 ? '添加金币' : '扣除金币'
  pointForm.reason = ''
  pointForm.changePoint = ''
  pointReasons.value = []

  if (flow === 0) {
    try {
      pointReasons.value = await fetchPointReasons(flow)
    } catch (cause) {
      ElMessage.error(getErrorMessage(cause) || '积分原因加载失败')
    }
  }
  pointDialogVisible.value = true
}

function queryPointReasons(
  queryString: string,
  callback: (items: Array<PointReason & { value: string }>) => void
) {
  const keyword = queryString.toLowerCase()
  const reasons = pointReasons.value
    .filter((item) => item.reason.toLowerCase().startsWith(keyword))
    .map((item) => ({ ...item, value: item.reason }))
  callback(reasons)
}

function selectPointReason(item: PointReason) {
  pointForm.reason = item.reason
  pointForm.changePoint = item.point
}

function validateForm(form: FormRef) {
  return new Promise<boolean>((resolve) => form.validate(resolve))
}

async function submitPointChange() {
  if (!(await validateForm(pointFormRef.value!))) return false
  await changeUserPoint(selectedUser.value!.id, {
    reason: pointForm.reason,
    changePoint: Number(pointForm.changePoint),
    type: pointFlow.value
  })
  return true
}

async function handlePointChangeSuccess() {
  ElMessage.success('金币操作成功')
  await refresh()
}

const recordDialogVisible = ref(false)
const recordDialogTitle = ref('金币流水')

async function openPointRecords(row: UserPointRecord, flow: PointFlow) {
  recordDialogTitle.value = flow === 0 ? '金币使用流水' : '金币增加流水'
  recordFilters.userId = row.id
  recordFilters.flow = flow
  recordDialogVisible.value = true
  await searchPointRecords()
}

const batchDialogVisible = ref(false)
const batchPoints = ref<number | ''>(100)
const uploadRef = ref()
const fileList = shallowRef<any[]>([])

function handleFileChange(_file: unknown, files: any[]) {
  fileList.value = files
}

async function readFirstColumn(file: File) {
  const workbook = XLSX.read(await file.arrayBuffer(), { type: 'array' })
  const sheet = workbook.Sheets[workbook.SheetNames[0]]
  const rows = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
    header: 1,
    defval: ''
  })
  return rows.map((row) => row[0]).filter((value) => value !== '')
}

async function submitBatchUpdate() {
  if (batchPoints.value === '') {
    ElMessage.error('金币数是必传的')
    return false
  }
  if (fileList.value.length === 0) {
    ElMessage.error('请先上传文件')
    return false
  }

  const excelDatas = await readFirstColumn(fileList.value[0].raw)
  if (excelDatas.length > 100) {
    ElMessage.error('一次最多修改100个客户')
    return false
  }
  if (excelDatas.length === 0) {
    ElMessage.error('未检测到客户昵称')
    return false
  }

  const result = await batchUpdateUserPoint({
    excelDatas,
    points: Number(batchPoints.value)
  })
  return showBatchResult(result)
}

function showBatchResult(result: BatchUpdateResult) {
  if (result.flag === false) {
    ElMessageBox.alert('添加失败，请重试', '提示', {
      confirmButtonText: '确认',
      type: 'error'
    })
    return false
  }

  const message =
    '<font color="red">添加完成，以下昵称重名，请手动为其添加积分：</font><br/>' +
    result.duplicates.toString() +
    '<br/><font color="red">以下昵称不存在：</font><br/>' +
    result.unknowns.toString()
  ElMessageBox.alert(message, '注意:', {
    confirmButtonText: '确认',
    type: 'warning',
    dangerouslyUseHTMLString: true
  })
  return true
}

async function handleBatchSuccess() {
  await refresh()
}

function resetBatchDialog() {
  uploadRef.value?.clearFiles()
  fileList.value = []
  batchPoints.value = 100
}

function handleSubmitError(cause: unknown) {
  ElMessage.error(getErrorMessage(cause) || '操作失败')
}

onMounted(() => {
  if (canList) load()
})

return (_ctx: any,_cache: any) => {
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_autocomplete = _resolveComponent("el-autocomplete")!
  const _component_el_form_item = _resolveComponent("el-form-item")!
  const _component_el_input = _resolveComponent("el-input")!
  const _component_el_dialog = _resolveComponent("el-dialog")!
  const _component_el_upload = _resolveComponent("el-upload")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_unref(CrudSearch), {
      model: _unref(filters),
      items: _unref(pointSearchItems),
      loading: _unref(loading),
      onSearch: _unref(search),
      onReset: _unref(reset)
    }, null, 8, ["model", "items", "loading", "onSearch", "onReset"]),
    _createVNode(_unref(CrudTable), {
      rows: _unref(records),
      "page-no": _unref(pageNo),
      "page-size": _unref(pageSize),
      total: _unref(total),
      loading: _unref(loading),
      "error-message": errorMessage.value,
      columns: _unref(userPointTableColumns),
      onPageChange: _unref(changePage),
      onPageSizeChange: _unref(changePageSize),
      onRetry: _unref(refresh)
    }, {
      title: _withCtx(() => [
        _createTextVNode("用户金币列表")
      ]),
      actions: _withCtx(() => [
        (_unref(canBatchEditPoint))
          ? (_openBlock(), _createBlock(_component_el_button, {
              key: 0,
              type: "primary",
              onClick: _cache[0] || (_cache[0] = ($event: any) => (batchDialogVisible.value = true))
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(Upload), {
                  class: "point-page__button-icon",
                  "aria-hidden": "true"
                }),
                _createTextVNode(" 批量添加积分 ")
              ]),
              _: 1
            }))
          : _createCommentVNode("", true)
      ]),
      rowActions: _withCtx(({ row }) => [
        _createElementVNode("div", _hoisted_2, [
          _createVNode(_component_el_button, {
            type: "text",
            onClick: ($event: any) => (openPointDialog(row, 1))
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(Coin), {
                class: "point-page__button-icon",
                "aria-hidden": "true"
              }),
              _createTextVNode(" 添加金币 ")
            ]),
            _: 2
          }, 1032, ["onClick"]),
          _createVNode(_component_el_button, {
            type: "text",
            onClick: ($event: any) => (openPointDialog(row, 0))
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(Coin), {
                class: "point-page__button-icon",
                "aria-hidden": "true"
              }),
              _createTextVNode(" 扣除金币 ")
            ]),
            _: 2
          }, 1032, ["onClick"]),
          _createVNode(_component_el_button, {
            type: "text",
            onClick: ($event: any) => (openPointRecords(row, 0))
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(Download), {
                class: "point-page__button-icon",
                "aria-hidden": "true"
              }),
              _createTextVNode(" 金币使用流水 ")
            ]),
            _: 2
          }, 1032, ["onClick"]),
          _createVNode(_component_el_button, {
            type: "text",
            onClick: ($event: any) => (openPointRecords(row, 1))
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(Upload), {
                class: "point-page__button-icon",
                "aria-hidden": "true"
              }),
              _createTextVNode(" 金币增加流水 ")
            ]),
            _: 2
          }, 1032, ["onClick"])
        ])
      ]),
      _: 1
    }, 8, ["rows", "page-no", "page-size", "total", "loading", "error-message", "columns", "onPageChange", "onPageSizeChange", "onRetry"]),
    _createVNode(_unref(CrudDialog), {
      modelValue: pointDialogVisible.value,
      "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((pointDialogVisible).value = $event)),
      title: pointDialogTitle.value,
      submit: submitPointChange,
      onSuccess: handlePointChangeSuccess,
      onSubmitError: handleSubmitError
    }, {
      default: _withCtx(() => [
        (selectedUser.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
              _createTextVNode(" 用户："),
              _createElementVNode("strong", null, _toDisplayString(selectedUser.value.nickName), 1),
              _createTextVNode(" 当前金币："),
              _createElementVNode("strong", null, _toDisplayString(selectedUser.value.point), 1)
            ]))
          : _createCommentVNode("", true),
        _createVNode(_unref(ElForm), {
          ref_key: "pointFormRef",
          ref: pointFormRef,
          model: pointForm,
          rules: pointRules,
          "label-width": "100px"
        }, {
          default: _withCtx(() => [
            (pointFlow.value === 0)
              ? (_openBlock(), _createBlock(_component_el_form_item, {
                  key: 0,
                  label: "原因",
                  prop: "reason"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_el_autocomplete, {
                      modelValue: pointForm.reason,
                      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((pointForm.reason) = $event)),
                      "fetch-suggestions": queryPointReasons,
                      clearable: "",
                      placeholder: "请输入原因",
                      style: {"width":"100%"},
                      onSelect: selectPointReason
                    }, null, 8, ["modelValue"])
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true),
            _createVNode(_component_el_form_item, {
              label: "变动金币数",
              prop: "changePoint"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_input, {
                  modelValue: pointForm.changePoint,
                  "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((pointForm.changePoint) = $event)),
                  modelModifiers: { number: true },
                  type: "number",
                  placeholder: "请输入金币数"
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            })
          ]),
          _: 1
        }, 8, ["model"])
      ]),
      _: 1
    }, 8, ["modelValue", "title"]),
    _createVNode(_component_el_dialog, {
      modelValue: recordDialogVisible.value,
      "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((recordDialogVisible).value = $event)),
      title: recordDialogTitle.value,
      width: "min(760px, calc(100vw - 32px))",
      "destroy-on-close": ""
    }, {
      default: _withCtx(() => [
        _createVNode(_unref(CrudTable), {
          rows: _unref(pointRecords),
          "page-no": _unref(recordPageNo),
          "page-size": _unref(recordPageSize),
          total: _unref(recordTotal),
          loading: _unref(recordLoading),
          "error-message": recordErrorMessage.value,
          columns: _unref(pointRecordTableColumns),
          onPageChange: _unref(changeRecordPage),
          onPageSizeChange: _unref(changeRecordPageSize),
          onRetry: _unref(searchPointRecords)
        }, null, 8, ["rows", "page-no", "page-size", "total", "loading", "error-message", "columns", "onPageChange", "onPageSizeChange", "onRetry"])
      ]),
      _: 1
    }, 8, ["modelValue", "title"]),
    _createVNode(_unref(CrudDialog), {
      modelValue: batchDialogVisible.value,
      "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((batchDialogVisible).value = $event)),
      title: "批量添加金币",
      submit: submitBatchUpdate,
      onSuccess: handleBatchSuccess,
      onSubmitError: handleSubmitError,
      onClosed: resetBatchDialog
    }, {
      default: _withCtx(() => [
        _createVNode(_unref(ElForm), { "label-width": "100px" }, {
          default: _withCtx(() => [
            _createVNode(_component_el_form_item, { label: "变动金币数" }, {
              default: _withCtx(() => [
                _createVNode(_component_el_input, {
                  modelValue: batchPoints.value,
                  "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((batchPoints).value = $event)),
                  modelModifiers: { number: true },
                  type: "number",
                  placeholder: "请输入金币数"
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            })
          ]),
          _: 1
        }),
        _createVNode(_component_el_upload, {
          ref_key: "uploadRef",
          ref: uploadRef,
          drag: "",
          action: "#",
          "auto-upload": false,
          "file-list": fileList.value,
          accept: ".xls,.xlsx",
          limit: 1,
          "on-change": handleFileChange
        }, {
          tip: _withCtx(() => [
            _hoisted_5
          ]),
          default: _withCtx(() => [
            _createVNode(_unref(Upload), {
              class: "point-page__upload-icon",
              "aria-hidden": "true"
            }),
            _hoisted_4
          ]),
          _: 1
        }, 8, ["file-list"])
      ]),
      _: 1
    }, 8, ["modelValue"])
  ]))
}
}

})